/**
 * Fabrique des blocs CMS avancé de la categorie "sections" (parite de rendu avec le
 * site public). Corps deplaces VERBATIM depuis config.tsx.
 *
 * i18n : AUCUN appel cmsNew(...) en portee module. Tout reste dans le corps
 * de la fabrique, re-evalue a chaque appel de createPuckConfig()
 * (useMemo([translationRevision])) pour reconstruire les libelles au
 * chargement des traductions i18next.
 */

import { MediaPickerField } from "@/components/cms-puck/fields/MediaPickerField";
import {
  imageClickableField,
  optionalLinkField,
} from "@/components/cms-puck/fields/media-interaction-fields";
import { withoutTextAlign, type BlockStyle } from "@/lib/cms-puck/block-style";
import {
  DEFAULT_BUTTON_LINK,
  DEFAULT_OPTIONAL_LINK,
  type ButtonLink,
} from "@/lib/cms-puck/blocks/button";
import { LinkTargetField } from "@/components/cms-puck/fields/LinkTargetField";
import { createTextImageContainerWrapper } from "@/lib/cms-puck/blocks/text-image-container";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { BannerBlock, CtaBlock } from "@/components/cms-puck/blocks/text-blocks";
import { TextImageBlock } from "@/components/cms-puck/blocks/media-blocks";
import { AccItemPreview, ContactFormPreview, TabsBlock } from "@/components/cms-puck/blocks/misc-blocks";
import { compositeInnerGapAlignFields } from "@/lib/cms-puck/layout";
import { type RawComponentsConfig } from "../types";
import { TEXT_IMAGE_SLOT_ALLOW } from "../assembly";

export function buildSectionsBlocks() {
  return {
    TextImage: {
      label: cmsNew("cms.new.auto.label.texte_image", "Texte + Image"),
      fields: {
        imagePosition: {
          type: "radio",
          label: cmsNew(
            "cms.new.auto.label.position_de_limage",
            "Position de l'image",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.droite", "Droite"),
              value: "right",
            },
          ],
        },
        imageAttachmentId: {
          type: "custom",
          label: cmsNew("cms.new.blocks.media.label", "Média"),
          render: ({ value, onChange }) => (
            <MediaPickerField
              value={value}
              onChange={onChange}
              fieldName="imageAttachmentId"
              label=""
            />
          ),
        },
        imageFrame: {
          type: "radio",
          label: cmsNew("cms.new.blocks.textImage.imageFrame", "Cadre de l'image"),
          options: [
            {
              label: cmsNew("cms.new.blocks.textImage.frameCard", "Encadrée"),
              value: "card",
            },
            {
              label: cmsNew("cms.new.blocks.textImage.frameNone", "Nue"),
              value: "none",
            },
          ],
        },
        container: {
          type: "radio",
          label: cmsNew("cms.new.blocks.textImage.container", "Largeur"),
          options: [
            {
              label: cmsNew("cms.new.blocks.textImage.widthContained", "Contenue"),
              value: "contained",
            },
            {
              label: cmsNew("cms.new.blocks.textImage.widthFull", "Pleine"),
              value: "full",
            },
          ],
        },
        paddingY: {
          type: "radio",
          label: cmsNew("cms.new.blocks.textImage.paddingY", "Marge verticale"),
          options: [
            {
              label: cmsNew("cms.new.blocks.textImage.padLg", "Grande"),
              value: "lg",
            },
            {
              label: cmsNew("cms.new.blocks.textImage.padSm", "Réduite"),
              value: "sm",
            },
            {
              label: cmsNew("cms.new.blocks.textImage.padNone", "Aucune"),
              value: "none",
            },
          ],
        },
        imageOnMobile: {
          type: "radio",
          label: cmsNew("cms.new.blocks.textImage.imageOnMobile", "Image en mobile"),
          options: [
            {
              label: cmsNew("cms.new.blocks.textImage.imageOnMobileShow", "Afficher"),
              value: "show",
            },
            {
              label: cmsNew("cms.new.blocks.textImage.imageOnMobileHide", "Masquer"),
              value: "hide",
            },
          ],
        },
        ...compositeInnerGapAlignFields(),
        imageCaption: {
          type: "text",
          label: cmsNew("cms.new.blocks.textImage.imageCaption", "Légende / crédits image (traduisible)"),
        },
        // Image cliquable → plein écran ; lien optionnel prioritaire (parité Media).
        clickable: imageClickableField(cmsNew),
        link: optionalLinkField(cmsNew),
        content: {
          type: "slot",
          label: cmsNew("cms.new.auto.label.colonne_texte", "Colonne texte"),
          allow: [...TEXT_IMAGE_SLOT_ALLOW],
        },
      },
      defaultProps: {
        imagePosition: "right",
        imageAttachmentId: "",
        imageFrame: "card",
        container: "contained",
        paddingY: "none",
        imageOnMobile: "show",
        gap: "md",
        alignItems: "center",
        imageCaption: "",
        clickable: true,
        link: { ...DEFAULT_OPTIONAL_LINK },
        content: [],
      },
      resolveData: (data) => {
        const blockId = String(data.props.id ?? "text-image");
        const raw = data.props.content;
        const title = String(data.props.title ?? "").trim();
        const body = String(data.props.body ?? "").trim();

        if (title || body) {
          const inner: { type: string; props: Record<string, unknown> }[] =
            [];
          if (title) {
            inner.push({
              type: "Heading",
              props: {
                id: `${blockId}-h`,
                level: "h2",
                text: title,
                align: "left",
              },
            });
          }
          if (body) {
            inner.push({
              type: "RichText",
              props: {
                id: `${blockId}-b`,
                body,
                align: "left",
              },
            });
          }
          return {
            props: {
              content: [createTextImageContainerWrapper(blockId, inner)],
            },
          };
        }

        if (Array.isArray(raw) && raw.length > 0) return { props: {} };

        return {
          props: {
            content: [createTextImageContainerWrapper(blockId)],
          },
        };
      },
      render: ({
        id,
        puck,
        imageAttachmentId,
        imagePosition,
        imageFrame,
        container,
        paddingY,
        gap,
        gapCustomPx,
        alignItems,
        imageCaption,
        imageOnMobile,
        content,
        ...rest
      }) => (
        <TextImageBlock
          id={id}
          puck={puck}
          imageAttachmentId={imageAttachmentId}
          sampleUrl={(rest as { sampleUrl?: string }).sampleUrl}
          imagePosition={imagePosition}
          imageFrame={imageFrame}
          container={container}
          paddingY={paddingY}
          gap={gap}
          gapCustomPx={gapCustomPx}
          alignItems={alignItems}
          imageCaption={imageCaption}
          imageOnMobile={imageOnMobile}
          content={content}
        />
      ),
    },

    Cta: {
      label: cmsNew("cms.new.blocks.cta.label", "Appel à l'action"),
      fields: {
        title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.titre_traduisible",
            "Titre (traduisible)",
          ),
          contentEditable: true,
        },
        text: {
          type: "textarea",
          label: cmsNew(
            "cms.new.auto.label.texte_traduisible",
            "Texte (traduisible)",
          ),
        },
        buttonLabel: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_du_bouton_traduisible",
            "Libellé du bouton (traduisible)",
          ),
        },
        // Lien structuré — l'ancien `buttonUrl` plat reste lu (resolveButtonLink).
        link: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.lien_du_bouton",
            "Lien du bouton",
          ),
          render: ({ value, onChange }) => (
            <LinkTargetField
              value={value as ButtonLink | undefined}
              onChange={onChange}
            />
          ),
        },
        variant: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.style_du_bouton_structurel",
            "Style du bouton (structurel)",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.principal", "Principal"),
              value: "primary",
            },
            {
              label: cmsNew("cms.new.auto.label.secondaire", "Secondaire"),
              value: "secondary",
            },
            {
              label: cmsNew("cms.new.auto.label.contour", "Contour"),
              value: "outline",
            },
          ],
        },
        align: {
          type: "radio",
          label: cmsNew("cms.new.auto.label.alignement", "Alignement"),
          options: [
            {
              label: cmsNew("cms.new.auto.label.gauche", "Gauche"),
              value: "left",
            },
            {
              label: cmsNew("cms.new.auto.label.centre", "Centré"),
              value: "center",
            },
          ],
        },
        ...compositeInnerGapAlignFields({ withAlignItems: false }),
      },
      defaultProps: {
        title: cmsNew(
          "cms.new.auto.title.pret_a_commencer",
          "Prêt à commencer ?",
        ),
        text: "",
        buttonLabel: "Nous contacter",
        link: { ...DEFAULT_BUTTON_LINK },
        variant: "primary",
        align: "center",
        gap: "md",
      },
      render: ({
        title,
        text,
        buttonLabel,
        variant,
        align,
        gap,
        gapCustomPx,
        ...rest
      }) => (
        <CtaBlock
          title={title}
          text={text}
          buttonLabel={buttonLabel}
          variant={variant}
          align={align}
          gap={gap}
          gapCustomPx={gapCustomPx}
          style={(rest as { style?: BlockStyle }).style}
        />
      ),
    },

    Banner: {
      label: cmsNew("cms.new.blocks.banner.label", "Bandeau"),
      fields: {
        text: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.message_traduisible",
            "Message (traduisible)",
          ),
          contentEditable: true,
        },
        linkLabel: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.libelle_du_lien_traduisible",
            "Libellé du lien (traduisible)",
          ),
        },
        // Lien structuré — l'ancien `linkUrl` plat reste lu (resolveButtonLink).
        link: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.lien", "Lien"),
          render: ({ value, onChange }) => (
            <LinkTargetField
              value={value as ButtonLink | undefined}
              onChange={onChange}
            />
          ),
        },
        tone: {
          type: "select",
          label: cmsNew(
            "cms.new.auto.label.ton_structurel",
            "Ton (structurel)",
          ),
          options: [
            {
              label: cmsNew("cms.new.auto.label.neutre", "Neutre"),
              value: "neutral",
            },
            {
              label: cmsNew("cms.new.auto.label.accent", "Accent"),
              value: "accent",
            },
            {
              label: cmsNew("cms.new.auto.label.sombre", "Sombre"),
              value: "dark",
            },
          ],
        },
        ...compositeInnerGapAlignFields(),
      },
      defaultProps: {
        text: "Message important à mettre en avant.",
        linkLabel: "",
        link: { ...DEFAULT_BUTTON_LINK },
        tone: "neutral",
        gap: "sm",
        alignItems: "center",
      },
      render: ({
        text,
        linkLabel,
        tone,
        gap,
        gapCustomPx,
        alignItems,
        ...rest
      }) => (
        <BannerBlock
          text={text}
          linkLabel={linkLabel}
          tone={tone}
          gap={gap}
          gapCustomPx={gapCustomPx}
          alignItems={alignItems}
          // Centré par construction : alignement de texte ignoré (parité front).
          style={withoutTextAlign((rest as { style?: BlockStyle }).style)}
        />
      ),
    },

    Accordion: {
      label: cmsNew("cms.new.blocks.accordion.label", "Accordéon (FAQ)"),
      fields: {
        item1Title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.question_1_traduisible",
            "Question 1 (traduisible)",
          ),
        },
        item1Body: { type: "slot" },
        item2Title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.question_2_traduisible",
            "Question 2 (traduisible)",
          ),
        },
        item2Body: { type: "slot" },
        item3Title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.question_3_traduisible",
            "Question 3 (traduisible)",
          ),
        },
        item3Body: { type: "slot" },
        item4Title: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.question_4_traduisible",
            "Question 4 (traduisible)",
          ),
        },
        item4Body: { type: "slot" },
      },
      defaultProps: {
        item1Title: "Question fréquente 1 ?",
        item1Body: [],
        item2Title: "Question fréquente 2 ?",
        item2Body: [],
        item3Title: "",
        item3Body: [],
        item4Title: "",
        item4Body: [],
      },
      // Aperçu éditeur : items toujours ouverts (les slots = DropZones éditables).
      // On n'affiche que les items à titre non vide. Réponse = on y dépose un
      // bloc « Texte riche » (ou autre primitive). Front : PuckAccordion (client).
      render: ({
        item1Title,
        item1Body: Body1,
        item2Title,
        item2Body: Body2,
        item3Title,
        item3Body: Body3,
        item4Title,
        item4Body: Body4,
      }) => (
        <div className="mx-auto max-w-3xl">
          {item1Title && (
            <AccItemPreview title={item1Title} body={<Body1 />} />
          )}
          {item2Title && (
            <AccItemPreview title={item2Title} body={<Body2 />} />
          )}
          {item3Title && (
            <AccItemPreview title={item3Title} body={<Body3 />} />
          )}
          {item4Title && (
            <AccItemPreview title={item4Title} body={<Body4 />} />
          )}
        </div>
      ),
    },

    Tabs: {
      label: cmsNew("cms.new.blocks.tabs.label", "Onglets"),
      fields: {
        tab1Title: {
          type: "text",
          label: cmsNew("cms.new.blocks.tabs.tab1", "Onglet 1 (traduisible)"),
        },
        tab1Body: { type: "slot" },
        tab2Title: {
          type: "text",
          label: cmsNew("cms.new.blocks.tabs.tab2", "Onglet 2 (traduisible)"),
        },
        tab2Body: { type: "slot" },
        tab3Title: {
          type: "text",
          label: cmsNew("cms.new.blocks.tabs.tab3", "Onglet 3 (traduisible)"),
        },
        tab3Body: { type: "slot" },
        tab4Title: {
          type: "text",
          label: cmsNew("cms.new.blocks.tabs.tab4", "Onglet 4 (traduisible)"),
        },
        tab4Body: { type: "slot" },
      },
      defaultProps: {
        tab1Title: "Onglet 1",
        tab1Body: [],
        tab2Title: "Onglet 2",
        tab2Body: [],
        tab3Title: "",
        tab3Body: [],
        tab4Title: "",
        tab4Body: [],
      },
      // Même rendu que PuckTabs (un onglet visible à la fois, cliquable dans le
      // canevas). Onglets à titre vide ignorés.
      render: ({
        id,
        puck,
        tab1Title,
        tab1Body: Body1,
        tab2Title,
        tab2Body: Body2,
        tab3Title,
        tab3Body: Body3,
        tab4Title,
        tab4Body: Body4,
      }) => (
        <TabsBlock
          id={id}
          isEditing={puck?.isEditing}
          items={[
            { title: tab1Title, body: <Body1 /> },
            { title: tab2Title, body: <Body2 /> },
            { title: tab3Title, body: <Body3 /> },
            { title: tab4Title, body: <Body4 /> },
          ]}
        />
      ),
    },

    ContactForm: {
      label: cmsNew(
        "cms.new.blocks.contactForm.label",
        "Formulaire de contact",
      ),
      fields: {
        recipientEmail: {
          type: "text",
          label: cmsNew(
            "cms.new.auto.label.email_destinataire_structurel",
            "Email destinataire (structurel)",
          ),
        },
      },
      defaultProps: {
        recipientEmail: "",
      },
      render: ({ recipientEmail }) => (
        <ContactFormPreview recipientEmail={recipientEmail} />
      ),
    },
  } satisfies Pick<RawComponentsConfig, "TextImage" | "Cta" | "Banner" | "Accordion" | "Tabs" | "ContactForm">;
}
